import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const filled = (...v) => v.every((x) => String(x ?? "").trim());
export const RULE_CHECKS = {
  name: (d) => filled(d.name),
  phone: (d) => filled(d.phone),
  emergency_contact: (d) => (d.family_contacts || []).some((c) => filled(c.name, c.phone)),
  address: (d) => filled(d.address),
  license: (d) => filled(d.license_number, d.license_expiry),
  license_photo: (d) => filled(d.license_photo_id),
  driver_photo: (d) => filled(d.driver_photo_id),
  pan: (d) => filled(d.pan_number),
  bank: (d) => filled(d.bank_account_number, d.bank_ifsc, d.bank_name, d.bank_holder),
};

export function useDriverRules(endpoint = "/driver-field-rules") {
  const [cfg, setCfg] = useState({ rules: { name: true }, labels: {} });
  useEffect(() => { api.get(endpoint).then((r) => setCfg(r.data)).catch(() => {}); }, [endpoint]);
  return { ...cfg, setCfg };
}

export const missingFor = (d, labels) => Object.keys(labels).filter((k) => !RULE_CHECKS[k](d));

export function CompletionBadge({ driver, labels, testid }) {
  const keys = Object.keys(labels);
  if (!keys.length) return null;
  const miss = missingFor(driver, labels);
  const pct = Math.round(((keys.length - miss.length) / keys.length) * 100);
  const color = pct === 100 ? "bg-emerald-500" : pct >= 60 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div data-testid={testid} className="mt-1 w-48" title={miss.length ? `Pending: ${miss.map((k) => labels[k]).join(", ")}` : "Profile complete"}>
      <div className="flex items-center gap-2">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-secondary"><div className={`h-full ${color}`} style={{ width: `${pct}%` }} /></div>
        <span className="text-[10px] font-semibold text-muted-foreground" data-testid={`${testid}-pct`}>{pct}% Completed</span>
      </div>
      {miss.length > 0 && <div className="whitespace-normal text-[10px] font-normal leading-tight text-amber-700 dark:text-amber-400">Pending: {miss.map((k) => labels[k]).join(", ")}</div>}
    </div>
  );
}

export function DriverRulesDialog({ cfg, onSaved, endpoint = "/driver-field-rules", title = "Driver Profile Fields", testid = "driver-field-settings", entity = "Driver" }) {
  const [open, setOpen] = useState(false);
  const [r, setR] = useState({});
  useEffect(() => { if (open) setR(cfg.rules); }, [open, cfg.rules]);
  const save = async () => {
    try { const { data } = await api.put(endpoint, { rules: r }); onSaved(data); toast.success("Field settings saved"); setOpen(false); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <Button variant="outline" data-testid={`${testid}-btn`} onClick={() => setOpen(true)}><Settings2 className="mr-1 h-4 w-4" />Mandatory / Optional</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-card" data-testid={`${testid}-dialog`}>
          <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>ON = Mandatory (*) — iske bina {entity} save nahi hoga. OFF = Optional (baad me bhar sakte hain).</DialogDescription></DialogHeader>
          <div className="divide-y rounded-lg border">
            {Object.entries(cfg.labels).map(([k, label]) => (
              <label key={k} className="flex items-center justify-between px-3 py-2.5 text-sm">
                <span>{label}{r[k] && <span className="text-rose-600"> *</span>}{k === "name" && <span className="ml-2 text-[11px] text-muted-foreground">(always required)</span>}</span>
                <Switch data-testid={`rule-${k}`} checked={!!r[k]} disabled={k === "name"} onCheckedChange={(v) => setR({ ...r, [k]: v })} />
              </label>
            ))}
          </div>
          <DialogFooter><Button data-testid={`${testid}-save`} onClick={save}>Save Settings</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
